import { isMac, styles } from "@repo/helpers";
import { Button, Dialog, DialogClose, DialogContent, DialogTitle } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { CornerDownLeft, LoaderCircle, LucideIcon, Search, SearchX, UserRound, X } from "lucide-react";
import { FC, KeyboardEvent as ReactKeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";

import * as Queries from "../../api/queries";
import { useRepSearchApi } from "../../api/repSearchApi";
import { useRepContext } from "../../contexts/editableItemContext";
import { useUpdateRep } from "../../helpers/urlHooks";
import RepPortalSearchItem from "../../models/repPortalSearchItem";
import { usePages } from "./hooks";
import { SearchResults, searchTerms } from "./searchResults";
import style from "./style.module.scss";

const AGENT_ENTITY = "Agents";
const RESULTS_PER_GROUP = 25;

interface EntityType {
	label: string;
	icon: LucideIcon;
}

interface ResultGroup extends EntityType {
	type: string;
	items: RepPortalSearchItem[];
	total: number;
}

interface SearchDialogProps {
	searchOpen: boolean;
	setSearchOpen: (open: boolean) => void;
}

export const SearchDialog: FC<SearchDialogProps> = ({ searchOpen, setSearchOpen }) => {
	const [search, setSearch] = useState("");
	const [activeIndex, setActiveIndex] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const baseId = useId();
	const listId = `${baseId}-results`;
	const optionId = (index: number) => `${baseId}-option-${index}`;
	const updateRep = useUpdateRep();
	const { repId } = useRepContext();
	const entityTypes = useEntityTypes();
	const { loadSearches } = useRepSearchApi();
	const repSearchQuery = useQuery({
		queryKey: Queries.REP_PORTAL_META_SEARCH,
		queryFn: async () => new SearchResults(await loadSearches()),
	});

	const terms = useMemo(() => searchTerms(search), [search]);
	const groups = useMemo(
		() => groupResults(repSearchQuery.data?.search(search) ?? [], terms[0], entityTypes),
		[repSearchQuery.data, search, terms, entityTypes],
	);
	const results = useMemo(() => groups.flatMap((g) => g.items), [groups]);
	const active = Math.min(activeIndex, results.length - 1);

	const searchableLabels = useMemo(() => {
		const types = new Set((repSearchQuery.data?.items ?? []).map((i) => i.entityType));
		return [...types].map((t) => entityTypes.get(t)?.label.toLowerCase() ?? t.toLowerCase());
	}, [repSearchQuery.data, entityTypes]);

	function changeOpen(open: boolean) {
		setSearchOpen(open);
		if (!open) updateSearch("");
	}

	function updateSearch(value: string) {
		setSearch(value);
		setActiveIndex(0);
	}

	async function openItem(item: RepPortalSearchItem) {
		changeOpen(false);
		if (item.entityType === AGENT_ENTITY) await updateRep(item.repId);
		else await updateRep(item.repId, item.entityType, item.entityId);
	}

	function moveActive(next: number) {
		if (!results.length) return;
		const index = (next + results.length) % results.length;
		setActiveIndex(index);
		requestAnimationFrame(() => document.getElementById(optionId(index))?.scrollIntoView({ block: "nearest" }));
	}

	function onInputKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
		if (e.key === "ArrowDown" || e.key === "ArrowUp") {
			e.preventDefault();
			moveActive(active + (e.key === "ArrowDown" ? 1 : -1));
		} else if (e.key === "Enter" && results[active]) {
			e.preventDefault();
			void openItem(results[active]);
		}
	}

	function renderBody() {
		if (repSearchQuery.isLoading) {
			return (
				<output className={style.paletteStatus}>
					<LoaderCircle className={styles(style.paletteStatusIcon, style.spin)} aria-hidden />
					<p>Loading search…</p>
				</output>
			);
		}

		if (repSearchQuery.isError) {
			return (
				<output className={style.paletteStatus}>
					<SearchX className={style.paletteStatusIcon} aria-hidden />
					<p className={style.paletteStatusTitle}>Search isn't available right now</p>
					<p>Try again in a moment.</p>
				</output>
			);
		}

		if (!terms.length) {
			return (
				<div className={style.paletteStatus}>
					<Search className={style.paletteStatusIcon} aria-hidden />
					<p className={style.paletteStatusTitle}>Jump to an agent or record</p>
					<p>
						Search {formatList(searchableLabels)} by name, login or ID.
						<span className={style.fineOnly}> Use ↑ ↓ and Enter to pick a result.</span>
					</p>
				</div>
			);
		}

		if (!results.length) {
			return (
				<output className={style.paletteStatus}>
					<SearchX className={style.paletteStatusIcon} aria-hidden />
					<p className={style.paletteStatusTitle}>No matches for “{search.trim()}”</p>
					<p>Check the spelling or try fewer words.</p>
				</output>
			);
		}

		let index = 0;
		return (
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- the results need custom markup that native select/option cannot render
			<div id={listId} role="listbox" aria-label="Search results" className={style.resultList}>
				{groups.map((group) => {
					const headingId = `${baseId}-${group.type}`;
					return (
						// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- the results need custom markup that native select/option cannot render
						<div key={group.type} role="group" aria-labelledby={headingId} className={style.resultGroup}>
							<div id={headingId} className={style.groupHeading}>
								<span>{group.label}</span>
								<span className={style.groupCount}>{group.total}</span>
							</div>
							{group.items.map((item) => {
								const itemIndex = index++;
								const isActive = itemIndex === active;
								const isCurrent = item.entityType === AGENT_ENTITY && item.repId === repId;
								return (
									<button
										key={`${item.entityType}-${item.entityId}`}
										id={optionId(itemIndex)}
										type="button"
										// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- the results need custom markup that native select/option cannot render
										role="option"
										tabIndex={-1}
										aria-selected={isActive}
										className={styles(style.result, isActive && style.resultActive)}
										onClick={() => openItem(item)}
										onPointerMove={(e) => {
											if (e.pointerType === "mouse" && !isActive) setActiveIndex(itemIndex);
										}}
									>
										<span className={style.resultIcon}>
											<group.icon aria-hidden />
										</span>
										<span className={style.resultTitle}>
											<Highlighted text={item.displayText ?? item.text} terms={terms} />
										</span>
										{isCurrent && <span className={style.resultBadge}>Current</span>}
										<CornerDownLeft className={style.resultEnter} aria-hidden />
									</button>
								);
							})}
							{group.total > group.items.length && (
								<p className={style.groupMore}>
									{group.total - group.items.length} more — keep typing to narrow it down
								</p>
							)}
						</div>
					);
				})}
			</div>
		);
	}

	return (
		<Dialog open={searchOpen} onOpenChange={changeOpen}>
			<DialogContent
				showCloseButton={false}
				aria-describedby={undefined}
				onOpenAutoFocus={(e) => {
					e.preventDefault();
					inputRef.current?.focus();
				}}
				className={style.paletteDialog}
			>
				<DialogTitle className={style.srOnly}>Search</DialogTitle>
				<div className={style.paletteHeader}>
					<Search className={style.paletteSearchIcon} aria-hidden />
					<input
						ref={inputRef}
						role="combobox"
						aria-expanded={results.length > 0}
						aria-controls={listId}
						aria-activedescendant={results[active] ? optionId(active) : undefined}
						aria-autocomplete="list"
						aria-label="Search agents and records"
						className={style.paletteInput}
						placeholder="Search by name, login or ID"
						value={search}
						onChange={(e) => updateSearch(e.target.value)}
						onKeyDown={onInputKeyDown}
						autoComplete="off"
						autoCorrect="off"
						spellCheck={false}
						enterKeyHint="go"
					/>
					{search && (
						<button
							type="button"
							className={style.clearButton}
							aria-label="Clear search"
							onClick={() => {
								updateSearch("");
								inputRef.current?.focus();
							}}
						>
							<X aria-hidden />
						</button>
					)}
					<DialogClose className={style.closeButton} aria-label="Close search">
						<span className={style.closeLabelTouch}>Cancel</span>
						<kbd className={style.closeLabelKey}>Esc</kbd>
					</DialogClose>
				</div>
				<div className={style.paletteBody}>{renderBody()}</div>
				{results.length > 0 && (
					<div className={style.paletteFooter} aria-hidden>
						<span>
							<kbd>↑</kbd>
							<kbd>↓</kbd> to navigate
						</span>
						<span>
							<kbd>↵</kbd> to open
						</span>
						<span>
							<kbd>Esc</kbd> to close
						</span>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
};

/** Maps each searchable entity type to the label and icon of its portal page, with Agents always included */
function useEntityTypes() {
	const pages = usePages();
	return useMemo(() => {
		const types = new Map<string, EntityType>([[AGENT_ENTITY, { label: "Agents", icon: UserRound }]]);
		for (const page of Object.values(pages).flat()) {
			if (!types.has(page.id)) types.set(page.id, { label: page.label, icon: page.icon });
		}
		return types;
	}, [pages]);
}

/** Ranks matches by the first search term, groups them by entity type with Agents first, and caps each group at RESULTS_PER_GROUP */
function groupResults(matches: RepPortalSearchItem[], firstTerm: string | undefined, types: Map<string, EntityType>) {
	const ranked = firstTerm ? matches.toSorted((a, b) => rank(a, firstTerm) - rank(b, firstTerm)) : matches;
	const groups: ResultGroup[] = [];
	for (const [type, items] of ranked.groupBy((i) => i.entityType)) {
		const entityType = types.get(type) ?? { label: type, icon: Search };
		groups.push({ type, ...entityType, items: items.slice(0, RESULTS_PER_GROUP), total: items.length });
	}
	return groups.toSorted((a, b) => Number(b.type === AGENT_ENTITY) - Number(a.type === AGENT_ENTITY));
}

/** Sort key for a match: 0 if the text starts with the term, 1 if a later word does, otherwise 2. Expects a lowercase term */
function rank(item: RepPortalSearchItem, term: string) {
	const text = (item.displayText ?? item.text).toLowerCase();
	if (text.startsWith(term)) return 0;
	if (text.includes(` ${term}`) || text.includes(`(${term}`)) return 1;
	return 2;
}

/** Joins labels into a readable list ("a, b and c"), falling back to "agents" when there are none */
function formatList(labels: string[]) {
	if (!labels.length) return "agents";
	if (labels.length === 1) return labels[0];
	return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

/** Renders text with every occurrence of the search terms wrapped in a mark, case insensitive */
function Highlighted({ text, terms }: { text: string; terms: string[] }) {
	if (!terms.length) return <>{text}</>;
	const pattern = new RegExp(
		`(${terms
			.toSorted((a, b) => b.length - a.length)
			.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
			.join("|")})`,
		"gi",
	);
	return (
		<>
			{text.split(pattern).map((part, i) =>
				i % 2 ? (
					<mark key={i} className={style.match}>
						{part}
					</mark>
				) : (
					part
				),
			)}
		</>
	);
}

/** Header button that opens the search dialog, also opened with Ctrl+K (⌘K on Apple devices) */
export const SearchButton = () => {
	const [searchOpen, setSearchOpen] = useState(false);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
				e.preventDefault();
				setSearchOpen(true);
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, []);

	return (
		<>
			<Button
				className={styles(style.searchButton, style.searchButtonMd)}
				aria-label="Search"
				aria-keyshortcuts={isMac ? "Meta+K" : "Control+K"}
				onClick={() => setSearchOpen(true)}
			>
				<div className={style.searchButtonContent}>
					<Search className={style.searchButtonIcon} />
					<span className={styles(style.searchButtonText, style.searchButtonTextMd)}>Search…</span>
				</div>
				<kbd className={styles(style.searchButtonShortcut, style.searchButtonShortcutMd)}>
					{isMac ? "⌘" : "Ctrl"} K
				</kbd>
			</Button>
			<SearchDialog searchOpen={searchOpen} setSearchOpen={setSearchOpen} />
		</>
	);
};
